import { Player } from './player';
import { IVideoPlayer, PlayOption, PlayerState, TIMER_TYPE, VideoPlayerMode } from '../../enums';
import { NAME, PAUSED_RETRY_COUNT } from '../constants';
import { delay, isUndefined } from '../../utils/utils';
import { PlayerEvent } from '../event';
import { isAutoPlayDialogShowing } from './autoplay-dialog';
import Logger from '../../utils/log/logger';
import { isRequestVideoFrameCallbackSupported } from '../rtc-detection';
import { IOS_MAIN_VERSION, IS_ANDROID, IS_CHROMIUM_128_TO_143, IS_FIREFOX, IS_IOS, IS_SAFARI, isRealIOS } from '../../utils/environment';
import Timer from '../../utils/timer';
export interface VideoAttr {
  style: {
    width?: string;
    height?: string;
    objectFit?: string;
    transform?: string;
  };
  autoplay?: 'autoplay',
  playsinline?: 'playsinline',
  muted?: boolean;
  id: string;
}

export interface VideoPlayerOptions {
  id: string;
  track: MediaStreamVideoTrack | null;
  container: HTMLElement | null;
  canvas?: HTMLCanvasElement;
  muted: boolean;
  objectFit?: 'contain' | 'cover' | 'fill';
  viewMirror?: boolean;
  log: Logger;
  enableLogTrackState?: boolean;
}
// 用户播放视频后，不代表界面上显示了video标签，显示与否需要调用show和hide方法
export class VideoPlayer extends Player<typeof NAME.VIDEO> implements IVideoPlayer {
  stat: {
    width?: number;
    height?: number;
    fps?: number; // 渲染帧率
  } = {};
  private _calculateTimeout = -1;
  protected viewMirror = false;
  objectFit: 'contain' | 'cover' | 'fill' = 'cover';
  container: HTMLElement | null;
  canvas?: HTMLCanvasElement;
  shouldRenderAlpha = false;
  private _preSize = {
    width: 0,
    height: 0
  };
  posterImg?: HTMLImageElement; // safari 和 firefox 在远端流没数据时，video.poster 没有正常渲染，这个 image 用来作为 poster 兜底。
  pipWindow?: PictureInPictureWindow;
  enterPIPPromise?: Promise<PictureInPictureWindow>;
  private _originContainerPosition?: string;
  private _isResettingSrcObject = false; // 标志位：是否正在重置 srcObject
  private _wrapper: HTMLDivElement | null = null; // Grid wrapper，用于让多个 video 重叠
  private _useWrapper = false; // 是否使用 wrapper 模式（用于直播场景无闪烁切换）
  constructor(options: VideoPlayerOptions) {
    super(options, NAME.VIDEO);
    this.mode = options.canvas ? VideoPlayerMode.CANVAS_FROM_ELEMENT : VideoPlayerMode.ELEMENT;
    this.container = options.container;
    this.canvas = options.canvas;
    if (!isUndefined(options.viewMirror)) {
      this.viewMirror = options.viewMirror;
    }
    if (!isUndefined(options.objectFit)) {
      this.objectFit = options.objectFit;
    }
    this.initializeElement();
  }
  get isPlaying() {
    if (this._state !== PlayerState.PLAYING) return false;
    if (this.element && this.element.paused) return false;
    return this.track?.readyState === 'live' && !this.track.muted;
  }



  initializeElement() {
    const element = document.createElement(NAME.VIDEO);
    if (this.track && this.mode !== VideoPlayerMode.CANVAS_WITHOUT_ELEMENT) element.srcObject = new MediaStream([this.track]);
    // set to true to ensure video autoplay is always successful
    element.muted = true;

    element.setAttribute('id', `video_${this.id}`);
    element.setAttribute('style', this.styleAttribute);
    if (this.canvas) this.canvas.setAttribute('style', this.styleAttribute);
    element.setAttribute('autoplay', 'autoplay');
    element.setAttribute('playsinline', 'playsinline');
    this.element = element;
    // 部分安卓播放成功前，会出现一个播放按钮，此时可以设置默认的 poster 来规避。
    if (IS_ANDROID) {
      element.poster = 'data:,';
    }
    // 将 video 添加到 wrapper 中（如果 wrapper 存在）
    this._appendToWrapper();
    this.bindElementEvents();
    this.calculateStat();
  }

  get styleAttribute() {
    // 使用 wrapper 模式时，使用 grid-area: 1/1 让所有 video 重叠在同一个 grid cell 中
    let styleAttribute = this._useWrapper
      ? `grid-area:1/1;width:100%;height:100%;object-fit:${this.objectFit};${this.shouldRenderAlpha ? '' : 'background-color:black'};`
      : `width:100%;height:100%;object-fit:${this.objectFit};${this.shouldRenderAlpha ? '' : 'background-color:black'};`;
    if (this.viewMirror) {
      // transform: https://bugs.webkit.org/show_bug.cgi?id=241303
      styleAttribute += 'transform:scaleX(-1);';
    }
    return styleAttribute;
  }

  /**
   * 设置直播/Wrapper 模式
   * - true: 使用 CSS Grid 布局让多个 video 重叠，实现无闪烁切换
   * - false: 普通模式，直接添加到 container
   * @param enable 是否启用直播模式
   */
  setLiveMode(enable: boolean) {
    if (this._useWrapper === enable) return;
    this._useWrapper = enable;

    // 更新 element 的 style
    if (this.elementToRender) {
      this.elementToRender.setAttribute('style', this.styleAttribute);
    }

    // 调整 element 的 DOM 位置
    if (this.container && this.elementToRender) {
      if (enable) {
        const wrapper = this._getOrCreateWrapper();
        // 插入到最前面，让当前 video 在其他已有 video 的背后
        wrapper.insertBefore(this.elementToRender, wrapper.firstChild);
      } else {
        this.container.appendChild(this.elementToRender);
        this._cleanupWrapper();
      }
    }
  }
  setContainer(container: HTMLElement | null) {
    if (this.container === container) return;
    const prevWrapper = this._wrapper;
    const prevContainer = this.container;
    this.container = container;
    this._pausedRetryCount = PAUSED_RETRY_COUNT; // 设置新的 container 重置恢复此时
    // 用于切换容器时，重新添加video标签到wrapper
    if (this.track && this.elementToRender) this._appendToWrapper();

    // 如果 element 已经从旧 wrapper 中迁出，且旧 wrapper 为空，则清理它，避免残留空节点
    if (prevWrapper && prevContainer && prevContainer !== this.container) {
      if (prevWrapper.isConnected && prevWrapper.children.length === 0) {
        prevWrapper.remove();
      }
    }
  }

  /**
   * 获取或创建 Grid wrapper，用于让多个 video 重叠实现无闪烁切换
   */
  private _getOrCreateWrapper(): HTMLDivElement {
    // 调用方应确保 container 存在，此处为防御性断言
    if (!this.container) throw new Error('[VideoPlayer] container is required');
    // 复用已有 wrapper（可能由其他 VideoPlayer 创建）
    let wrapper = this.container.querySelector<HTMLDivElement>('[data-trtc-video-wrapper]');
    if (!wrapper) {
      wrapper = document.createElement('div');
      wrapper.setAttribute('data-trtc-video-wrapper', 'true');
      wrapper.style.cssText = 'display:grid;width:100%;height:100%;';
      this.container.appendChild(wrapper);
    }
    this._wrapper = wrapper;
    return wrapper;
  }

  /**
   * 将 video/canvas 添加到容器中
   * - wrapper 模式：使用 Grid 布局让多个 video 重叠，实现无闪烁切换
   * - 普通模式：直接添加到 container
   */
  private _appendToWrapper(element?: HTMLElement | null) {
    const el = element ?? this.elementToRender;
    if (!this.container || !el) return;
    if (this._useWrapper) {
      const wrapper = this._getOrCreateWrapper();
      // 插入到最前面，让新 video 在旧 video 背后（z-index 更低）
      wrapper.insertBefore(el, wrapper.firstChild);
    } else {
      // 普通模式：直接添加到 container
      this.container.appendChild(el);
    }
  }
  bindElementEvents(): any {
    const eventMananger = super.bindElementEvents();
    this.handleElementEvent = this.handleElementEvent.bind(this);
    this.handleFullscreenChange = this.handleFullscreenChange.bind(this);
    this.handleVolumeChange = this.handleVolumeChange.bind(this);
    if (eventMananger) {
      eventMananger.add(NAME.ENTER_PICTURE_IN_PICTURE, this.handleElementEvent)
        .add(NAME.LEAVE_PICTURE_IN_PICTURE, this.handleElementEvent)
        .add(NAME.RESIZE, this.handleElementEvent);
    }
    // fullscreenchange 事件需要在 document 上监听
    if (this.element) {
      this.element.addEventListener(NAME.FULLSCREEN_CHANGE, this.handleFullscreenChange);
      // iOS Safari 使用 webkitbeginfullscreen 和 webkitendfullscreen 事件
      this.element.addEventListener('webkitbeginfullscreen', this.handleFullscreenChange);
      this.element.addEventListener('webkitendfullscreen', this.handleFullscreenChange);
      this.element.addEventListener('volumechange', this.handleVolumeChange);
    }
  }

  handleTrackEvent(event: any) {
    if (event.type === NAME.MUTE) {
      if (this.stat?.fps) this.stat.fps = 0;
      if (this.isFullscreen()) {
        // Android Chrome 128 ~ 143 在全屏暂停恢复播放，会出现解码异常的问题，需要重设 srcObject 来规避
        this.resetSrcObjectToReplay();
      }
    }
    return super.handleTrackEvent(event);
  }


  handleFullscreenChange() {
    const isFullscreen = this.isFullscreen();
    if (isFullscreen) {
      this._log.info('enter fullscreen');
      this.emit(PlayerEvent.ENTER_FULL_SCREEN);
    } else {
      this._log.info('leave fullscreen');
      this.emit(PlayerEvent.LEAVE_FULL_SCREEN);
    }
  }

  handleVolumeChange() {
    if (this.isPictureInPicture() || this.isFullscreen()) {
      // iOS 全屏时，在全屏窗口设置 muted，需要把 muted 状态同步到 audioPlayer 中
      this.emit(PlayerEvent.VOLUME_CHANGE, { muted: this.element?.muted });
    }
  }

  handleElementEvent(event: any) {
    if (this.mode === VideoPlayerMode.CANVAS_WITHOUT_ELEMENT) return;
    super.handleElementEvent(event);
    const eventType = event.type;
    const isPIP = this.isPictureInPicture();
    const isFullScreen = this.isFullscreen();
    const isUserActionInPIPOrFullScreen = event.isTrusted && ((isPIP && IS_SAFARI) || isFullScreen);
    if (eventType === NAME.PLAYING && isUserActionInPIPOrFullScreen && !this._isResettingSrcObject) {
      this._log.warn(`user resume in ${isFullScreen ? 'fullscreen' : 'pip'}`);
      this.emit(PlayerEvent.USER_RESUME_IN_PIP_OR_FULL_SCREEN);
    }
    if (eventType === NAME.PAUSE) {
      if (isUserActionInPIPOrFullScreen) {
        this._log.warn(`user pause in ${isFullScreen ? 'fullscreen' : 'pip'}`);
        this.emit(PlayerEvent.USER_PAUSE_IN_PIP_OR_FULL_SCREEN);
      }
      if (this.container && !this.container.isConnected) {
        this._log.warn(`${this.kind} player has been remove, element ID: ${this.container.id}`);
        // 延迟 500ms 后，如果客户重新把 container 添加到 DOM 中，则重置 pausedRetryCount。
        delay(500).then(() => {
          if (this.container?.isConnected) {
            this._pausedRetryCount = PAUSED_RETRY_COUNT;
            this._log.info(`view container ${this.container.id} is in dom, reset pausedRetryCount`);
          }
        });
      }

      if (this._pausedRetryCount > 0 && !isAutoPlayDialogShowing() && !this.isPausedByUserCall && !isUserActionInPIPOrFullScreen) {
        // 视频播放出现 pause 时，自动调用 resume 尝试恢复。
        // 1. iOS 15.0 视频播放会黑屏，调用 resume 能恢复。
        // 2. iOS 15.1 使用 canvas 二次采集时，若 video element 不可视时，video 会 paused，进而导致 canvas 采集不到数据。
        //    此时调用 resume 能恢复。
        // 自动播放弹窗显示时，不执行 resume 逻辑，等用户点击确认按钮后再恢复。以避免 iOS 上可能出现弹窗确认后，视频任然黑屏的问题。
        this._log.info(`[${PAUSED_RETRY_COUNT - this._pausedRetryCount + 1}/${PAUSED_RETRY_COUNT}] ${this.kind} player auto resume when paused`);
        this.doResume();
        this._pausedRetryCount--;
      }


      // iOS 14.4 闹铃结束时，会触发 video paused，sdk 执行 resume 后，还是处于 paused 状态。
      // 此时，在此处加个定时器，定时执行 resume，当播放成功后清理定时器。
      if (IS_IOS && !isUserActionInPIPOrFullScreen) {
        this._interval = Timer.run(TIMER_TYPE.TIMEOUT, () => {
          if (this.element && this._state === PlayerState.PAUSED && !this.isPausedByUserCall) {
            this.doResume();
          }
        }, { delay: 3000 });
      }
      if (this.stat.fps) this.stat.fps = 0;
    }
    if (this.viewMirror && this.element) {
      // 暂存之前样式，避免把业务侧添加的样式覆盖。
      const prevTransformStyle = this.element.style.transform;
      if (eventType === NAME.ENTER_PICTURE_IN_PICTURE) {
        this.element.style.transform = prevTransformStyle.replace('scaleX(-1)', '');
      } else if (eventType === NAME.LEAVE_PICTURE_IN_PICTURE && !prevTransformStyle.includes('scaleX')) {
        this.element.style.transform = `${prevTransformStyle} scaleX(-1)`;
      }
    }
    if (eventType === NAME.RESIZE) {
      // 注意：在首次播放时，会触发两次 resize 事件，两次的宽高一致。
      if (this._preSize.height !== this.element?.videoHeight || this._preSize.width !== this.element?.videoWidth) {
        this._log.info(`video size changed to ${this.element?.videoWidth}x${this.element?.videoHeight}`);
        this._preSize.height = this.element?.videoHeight || 0;
        this._preSize.width = this.element?.videoWidth || 0;
        this.emit(PlayerEvent.RESIZE, { newWidth: this._preSize.width, newHeight: this._preSize.height });
      }
    }

    if (eventType === NAME.LEAVE_PICTURE_IN_PICTURE) {
      this._log.warn('exit pip');
      if (this.isPaused && !this.isPausedByUserCall) {
        this._log.warn('resume after exit pip');
        this.doResume();
      }
      this.resetSrcObjectToReplay();
      this.emit(PlayerEvent.LEAVE_PICTURE_IN_PICTURE);
    }
    if (eventType === NAME.ENTER_PICTURE_IN_PICTURE) {
      this.emit(PlayerEvent.ENTER_PICTURE_IN_PICTURE);
    }
  }

  resetSrcObjectToReplay() {
    if (IS_ANDROID && IS_CHROMIUM_128_TO_143 && this.isPlayCalled && this.element && this.track && !this.isPausedByUserCall) {
      this._log.warn('reset srcObject to replay for android chromium');
      this._isResettingSrcObject = true;
      this.element.srcObject = new MediaStream([this.track]);
      this.element.play().catch(error => {
        this._log.warn('play failed after reset srcObject', error);
      }).finally(() => {
        this._isResettingSrcObject = false;
      });
    }
  }

  setCanvas(canvas?: HTMLCanvasElement, mode: VideoPlayerMode = VideoPlayerMode.CANVAS_FROM_ELEMENT) {
    if (this.canvas === canvas) return;
    this.canvas?.remove();
    canvas?.setAttribute('style', this.styleAttribute);
    this.canvas = canvas;
    this.mode = canvas ? mode : VideoPlayerMode.ELEMENT;
    if (this.mode === VideoPlayerMode.CANVAS_WITHOUT_ELEMENT) {
      this.setTrack(canvas!.captureStream().getVideoTracks()[0]);
    }
    if (canvas) {
      this.element?.remove();
      this._appendToWrapper(canvas);
    } else if (this.element) {
      this._appendToWrapper(this.element);
    }
  }
  setAttr(attr: VideoAttr): void {
    const _attr = Object.assign({ autoplay: 'autoplay', playsinline: 'playsinline', muted: true }, attr);
    _attr.style = Object.assign({ width: '100%', height: '100%' }, _attr.style);
    super.setAttr(_attr);
  }

  get mirror() {
    return this.viewMirror;
  }
  setRect(width: number, height: number) {
    if (this.elementToRender) {
      this.elementToRender.style.width = `${width}px`;
      this.elementToRender.style.height = `${height}px`;
    }
  }

  setViewMirror(mirror: boolean) {
    if (this.elementToRender) {
      this.elementToRender.style.transform = mirror ? 'scaleX(-1)' : '';
    }
    this.viewMirror = mirror;
  }

  setObjectFit(objectFit: 'contain' | 'cover' | 'fill') {
    if (this.elementToRender) {
      this.elementToRender.style.objectFit = `${objectFit}`;
    }
    this.objectFit = objectFit;
  }
  setPoster(poster: string, callByPauseResume = false) {
    return new Promise<void>(resolve => {
      if (!this.element) return resolve();
      this._log.info('setPoster', poster.slice(0, 10));
      if (poster === '') {
        this.element.removeAttribute('poster');
      } else {
        this.element.poster = poster;
      }

      const isNeedUpdatePosterImg = callByPauseResume && (IS_SAFARI || IS_FIREFOX);
      if (!isNeedUpdatePosterImg) return resolve();

      // 在 safari 和 firefox 中，停止订阅视频后，video.poster 没有正常渲染，此时使用 image 作为 poster 的兜底，避免出现暂停后黑屏的问题。
      if (poster === '') {
        this.removePosterImg();
        return resolve();
      }
      if (this.posterImg) return resolve();
      const img = document.createElement('img');
      img.src = poster;
      const videoStyle = window.getComputedStyle(this.element);
      const objectFit = videoStyle.objectFit || this.objectFit;
      // wrapper 模式使用 grid-area + z-index 确保 posterImg 显示在 video 上层，普通模式使用绝对定位
      let posterZIndex = 1;
      if (this._useWrapper) {
        const videoZIndex = parseInt(videoStyle.zIndex, 10);
        if (!isNaN(videoZIndex)) {
          posterZIndex = videoZIndex + 1;
        }
      }
      img.style.cssText = this._useWrapper
        ? `grid-area:1/1;z-index:${posterZIndex};width:100%;height:100%;object-fit:${objectFit};`
        : `position:absolute;top:0;left:0;width:100%;height:100%;object-fit:${objectFit};`;

      img.onload = async () => {
        try {
          if (img.decode) await img.decode();
          if (this.container && !this._useWrapper) {
            // 普通模式需要设置 container 为 relative 定位
            const style = window.getComputedStyle(this.container);
            if (style.position === 'static') {
              this._originContainerPosition = this.container.style.position;
              this.container.style.position = 'relative';
            }
          }
          this.posterImg = img;
          // wrapper 模式添加到 wrapper，普通模式添加到 container
          const parent = this._useWrapper ? this._wrapper : this.container;
          parent?.appendChild(img);
          if (isRealIOS() && IOS_MAIN_VERSION <= 17 && this.elementToRender) {
            this.elementToRender.style.visibility = 'hidden';
          }
        } catch (error) {
          this._log.warn('decode poster image error', error);
        }
        return resolve();
      };
      img.onerror = () => {
        this._log.warn('load poster image error');
        return resolve();
      };
    });
  }

  removePosterImg() {
    if (this.posterImg) {
      if (isRealIOS() && IOS_MAIN_VERSION <= 17 && this.elementToRender) {
        this.elementToRender.style.visibility = '';
      }
      this.posterImg.remove();
      URL.revokeObjectURL(this.posterImg.src);
      // 普通模式下恢复 container 的 position 样式
      if (!this._useWrapper && this.container && !isUndefined(this._originContainerPosition) && this.container.style.position === 'relative') {
        this.container.style.position = this._originContainerPosition;
      }
      delete this.posterImg;
    }
  }

  get hasPoster() {
    return !!this.posterImg || !!this.element?.getAttribute('poster');
  }

  async pause(usePosterForSafariOrFirefox = true): Promise<void> {
    super.pause();
    if (!this.isPictureInPicture() && !this.hasPoster && (IS_CHROMIUM_128_TO_143 || (usePosterForSafariOrFirefox && (IS_FIREFOX || IS_SAFARI)))) {
      await this.setPoster(this.getVideoFrame(), true);
    }
  }
  resume(byUserCall = false) {
    return super.resume(byUserCall).then(() => {
      if (this.posterImg || this.element?.poster) this.setPoster('', true);
    });
  }

  doResume(byUserCall = false): Promise<void> {
    // 规避 Chrome 128+ 版本 resume webrtc 流会导致播放卡一下的问题。
    if (this.isPaused && byUserCall && this.element && this.track && IS_CHROMIUM_128_TO_143 && this.track.kind === 'video') {
      this.element.srcObject = new MediaStream([this.track]);
    }
    return super.doResume();
  }
  stop(jitterBufferDelay = 0) {
    if (this.isPictureInPicture()) this.exitPictureInPicture().catch(err => {});
    if (this.isFullscreen()) this.exitFullscreen().catch(err => {});
    if (this.element) {
      this.element.removeEventListener(NAME.FULLSCREEN_CHANGE, this.handleFullscreenChange);
      this.element.removeEventListener('webkitbeginfullscreen', this.handleFullscreenChange);
      this.element.removeEventListener('webkitendfullscreen', this.handleFullscreenChange);
      this.element.removeEventListener('volumechange', this.handleVolumeChange);
    }
    super.stop(jitterBufferDelay);
    this.canvas?.remove();
    this.removePosterImg();
    if (this._useWrapper) {
      this._cleanupWrapper();
    }
  }

  /**
   * 清理 wrapper：如果 wrapper 中没有其他子元素，则移除 wrapper
   */
  private _cleanupWrapper() {
    if (this._wrapper && this._wrapper.children.length === 0) {
      this._wrapper.remove();
    }
    this._wrapper = null;
  }
  play(options?: PlayOption) {
    // 根据 options.isLiveStream 设置直播模式
    if (!isUndefined(options?.isLiveStream)) {
      this.setLiveMode(options.isLiveStream);
    }

    if (!this.element) {
      this.initializeElement();
    } else if (this.elementToRender && this.container) {
      // 确保 element 在正确的父元素中
      if (this._useWrapper) {
        const wrapper = this._getOrCreateWrapper();
        if (this.elementToRender.parentElement !== wrapper) {
          wrapper.insertBefore(this.elementToRender, wrapper.firstChild);
        }
      } else {
        if (this.elementToRender.parentElement !== this.container) {
          this.container.append(this.elementToRender);
        }
      }
    }
    if (this.mode === VideoPlayerMode.CANVAS_WITHOUT_ELEMENT) return Promise.resolve();
    return super.play();
  }
  get elementToRender() {
    return this.canvas || this.element;
  }
  setTrack(track: MediaStreamVideoTrack | null) {
    if (track !== this.track) {
      this.unbindTrackEvents();
      this.track = track;
      this.emit(PlayerEvent.MEDIA_TRACK_CHANGED, track);
      if (track !== null) {
        this.bindTrackEvents();
        if (this.element && this.mode !== VideoPlayerMode.CANVAS_WITHOUT_ELEMENT) {
          this.element.srcObject = new MediaStream([track]);
          this.element.remove();
        }
        // 将 video 添加到 wrapper 中
        this._appendToWrapper();
      }
    }
  }
  getVideoFrame() {
    if (this.canvas) return this.canvas.toDataURL('image/png');
    if (!this.element) {
      return '';
    }
    const canvas = document.createElement('canvas');
    canvas.width = this.element.videoWidth;
    canvas.height = this.element.videoHeight;
    const context = canvas.getContext('2d');
    context!.drawImage(this.element, 0, 0);
    return canvas.toDataURL('image/png');
  }
  // getVideoFrameBlob() {
  //   return new Promise<Blob|null>((resolve, reject) => {
  //     if (this.canvas) return this.canvas.toBlob(resolve, 'image/png');
  //     if (!this.element) return resolve(null);
  //     const canvas = document.createElement('canvas');
  //     canvas.width = this.element.videoWidth;
  //     canvas.height = this.element.videoHeight;
  //     const context = canvas.getContext('2d');
  //     context!.drawImage(this.element, 0, 0);
  //     return canvas.toBlob(resolve, 'image/png');
  //   }).then(blob => (blob ? URL.createObjectURL(blob) : ''));
  // }

  // @ts-ignore
  getElement() {
    return this.element;
  }

  calculateStat() {
    try {
      if (isRequestVideoFrameCallbackSupported() && this.element && this._calculateTimeout < 0) {
        let prevTime = 0;
        let prevMetaData: VideoFrameCallbackMetadata | null = null;
        const onVideoFrame = (time: DOMHighResTimeStamp, metadata: VideoFrameCallbackMetadata) => {
          this.stat.width = metadata.width;
          this.stat.height = metadata.height;
          if (prevMetaData) {
            this.stat.fps = Math.round((metadata.presentedFrames - prevMetaData.presentedFrames) / (time - prevTime) * 1000);
          }
          prevTime = time;
          prevMetaData = metadata;
          this._calculateTimeout = -1;
          if (this.element) {
            this._calculateTimeout = setTimeout(() => this.element?.requestVideoFrameCallback(onVideoFrame), 2000);
          }
        };
        this.element.requestVideoFrameCallback(onVideoFrame);
      }
    } catch (error) {
      this._log.warn('init stat failed', error);
    }
  }

  /**
   * 进入全屏模式
   * @returns {Promise<void>} 全屏请求的 Promise
   * @description 兼容不同浏览器的全屏 API，支持 video 和 canvas 元素
   */
  async enterFullscreen(): Promise<void> {
    const element = this.elementToRender;
    if (!element) {
      this._log.warn('no element to render, cannot enter fullscreen');
      throw new Error('No element available for fullscreen');
    }

    // IOS 在画中画中尝试进入全屏，可能会失败。若处于画中画状态，先退出画中画再全屏
    if (IS_IOS && this.isPictureInPicture()) {
      this._log.info('exit pip before entering fullscreen');
      try {
        await this.exitPictureInPicture();
      } catch (e) {
        // iOS 16 在某些状态下退出画中画会报 InvalidStateError，忽略此错误继续尝试全屏
        this._log.warn('exit pip failed before fullscreen:', e);
      }
    }

    try {
      // 标准 API
      if (element.requestFullscreen) {
        await element.requestFullscreen();
      } else if ((element as any).webkitRequestFullscreen) {
        // Webkit (Safari)
        await (element as any).webkitRequestFullscreen();
      } else if ((element as any).webkitEnterFullscreen) {
        // Webkit (older Safari)
        await (element as any).webkitEnterFullscreen();
      } else if ((element as any).mozRequestFullScreen) {
        // Mozilla
        await (element as any).mozRequestFullScreen();
      } else if ((element as any).msRequestFullscreen) {
        // Microsoft
        await (element as any).msRequestFullscreen();
      } else {
        throw new Error('Fullscreen API not supported');
      }
      this._log.info('entered fullscreen mode');
    } catch (error) {
      this._log.error('failed to enter fullscreen:', error);
      throw error;
    }
  }

  /**
   * 退出全屏模式
   * @returns {Promise<void>} 退出全屏请求的 Promise
   * @description 兼容不同浏览器的退出全屏 API
   */
  async exitFullscreen(): Promise<void> {
    try {
      if (!this.isFullscreen()) return;
      // 标准 API
      if (document.exitFullscreen) {
        await document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        // Webkit
        await (document as any).webkitExitFullscreen();
      } else if ((document as any).mozCancelFullScreen) {
        // Mozilla
        await (document as any).mozCancelFullScreen();
      } else if ((document as any).msExitFullscreen) {
        // Microsoft
        await (document as any).msExitFullscreen();
      } else {
        throw new Error('Exit fullscreen API not supported');
      }
      this._log.info('exited fullscreen mode');
    } catch (error) {
      this._log.error('failed to exit fullscreen:', error);
      throw error;
    }
  }

  /**
   * 检查是否处于全屏状态
   * @returns {boolean} 是否处于全屏状态
   */
  isFullscreen(): boolean {
    const element = this.elementToRender;
    if (!element) return false;

    // iOS Safari 使用 webkitDisplayingFullscreen 属性判断 video 元素是否全屏
    if (this.element && (this.element as any).webkitDisplayingFullscreen) {
      // iOS 在全屏进入画中画，webkitDisplayingFullscreen 还会返回 true，此时根据画中画状态判断来做兼容
      if (this.isPictureInPicture()) return false;
      return true;
    }

    const fullscreenElement = document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement ||
      (document as any).msFullscreenElement;

    return fullscreenElement === element;
  }

  /**
   * 切换全屏状态
   * @returns {Promise<void>} 切换全屏状态的 Promise
   * @description 如果当前是全屏状态则退出，否则进入全屏
   */
  async toggleFullscreen(): Promise<void> {
    if (this.isFullscreen()) {
      await this.exitFullscreen();
    } else {
      await this.enterFullscreen();
    }
  }

  /**
   * 进入画中画模式
   * @returns {Promise<PictureInPictureWindow>} 画中画窗口对象
   * @description 兼容不同浏览器的画中画 API，仅支持 video 元素
   */
  async enterPictureInPicture(): Promise<PictureInPictureWindow> {
    this.enterPIPPromise = this._enterPictureInPicture();
    try {
      return await this.enterPIPPromise;
    } finally {
      delete this.enterPIPPromise;
    }
  }

  private async _enterPictureInPicture(): Promise<PictureInPictureWindow> {
    try {
      if (!this.element) {
        throw new Error('No video element available for pip');
      }

      // 画中画仅支持 video 元素
      if (this.canvas && this.mode !== VideoPlayerMode.CANVAS_FROM_ELEMENT) {
        throw new Error('pip is not supported for canvas-only mode');
      }

      const { element } = this;

      // 标准 API
      if (element.requestPictureInPicture) {
        this._log.info('requestPictureInPicture');
        const pipWindow = await element.requestPictureInPicture();
        this.pipWindow = pipWindow;
        this._log.info('entered pip mode');
        if (this.elementToRender === this.canvas) {
          this.canvas.remove();
          this._appendToWrapper(this.element);
        }
        return pipWindow;
      }
      if ((element as any).webkitSetPresentationMode) {
        // Webkit (Safari)
        this._log.info('webkitSetPresentationMode');
        await (element as any).webkitSetPresentationMode('picture-in-picture');
        this._log.info('entered pip mode (webkit)');
        return {} as PictureInPictureWindow;
      }
      throw new Error('pip API not supported');
    } catch (error: any) {
      this._log.error('failed to enter pip:', error.name, error.message);
      throw error;
    }
  }

  /**
   * 退出画中画模式
   * @returns {Promise<void>} 退出画中画请求的 Promise
   * @description 兼容不同浏览器的退出画中画 API
   */
  async exitPictureInPicture(): Promise<void> {
    try {
      if (!this.isPictureInPicture()) return;
      delete this.pipWindow;
      // 标准 API
      if (document.pictureInPictureElement && document.exitPictureInPicture) {
        await document.exitPictureInPicture();
        if (this.elementToRender === this.canvas) {
          this.element?.remove(); // 移除 video 元素必定导致 paused，此时重置 resume 重试次数
          this._pausedRetryCount = PAUSED_RETRY_COUNT;
          this._appendToWrapper(this.canvas);
        }
        this._log.info('exited pip mode');
      } else if (this.element && (this.element as any).webkitSetPresentationMode) {
        // Webkit (Safari)
        await (this.element as any).webkitSetPresentationMode('inline');
        this._log.info('exited pip mode (webkit)');
      } else {
        throw new Error('Exit pip API not supported or not in PiP mode');
      }
    } catch (error) {
      this._log.error('failed to exit pip:', error);
      throw error;
    }
  }
  /**
   * 检查是否处于画中画状态
   * @returns {boolean} 是否处于画中画状态
   */
  isPictureInPicture(): boolean {
    if (!this.element) return false;

    const { element } = this;

    // 标准 API
    if (document.pictureInPictureElement) {
      return document.pictureInPictureElement === element;
    }

    // Webkit (Safari)
    if ((element as any).webkitPresentationMode) {
      return (element as any).webkitPresentationMode === 'picture-in-picture';
    }

    return false;
  }

  /**
   * 切换画中画状态
   * @returns {Promise<void>} 切换画中画状态的 Promise
   * @description 如果当前是画中画状态则退出，否则进入画中画
   */
  async togglePictureInPicture(): Promise<void> {
    if (this.isPictureInPicture()) {
      await this.exitPictureInPicture();
    } else {
      await this.enterPictureInPicture();
    }
  }
}
